import { useState, useEffect } from 'react'
import { useTelegram } from '@/shared/lib/telegram'
import {
  getStoredTheme,
  setStoredTheme,
  applyTheme,
  type Theme,
} from '@/shared/lib/theme'
import { subscribeToVkTheme } from '@/shared/lib/telegram/vk-bridge-adapter'

/**
 * Хук для работы с темой приложения.
 *
 * Режимы:
 *   'platform' (по умолчанию) — следовать теме VK/Telegram
 *   'light' / 'dark'          — явный выбор пользователя
 *
 * В VK слушает VKWebAppUpdateConfig через subscribeToVkTheme.
 * В Telegram — через colorScheme из контекста (обновляется провайдером по themeChanged).
 */
export function useTheme() {
  const { webApp } = useTelegram()
  const isVk = !!(webApp as any)?.isVk

  // Начальная тема платформы — из адаптера или Telegram WebApp
  const [platformTheme, setPlatformTheme] = useState<'light' | 'dark'>(() => {
    return ((webApp as any)?.colorScheme || 'light') as 'light' | 'dark'
  })

  const [theme, setThemeState] = useState<Theme>(() => {
    return getStoredTheme() || 'platform'
  })

  // В VK: подписываемся на VKWebAppUpdateConfig через наш pub/sub
  useEffect(() => {
    if (!isVk) return
    return subscribeToVkTheme((newTheme) => {
      setPlatformTheme(newTheme)
    })
  }, [isVk])

  // В Telegram: colorScheme живёт в context и меняется через themeChanged → setCurrentTheme
  // Синхронизируем platformTheme если webApp.colorScheme поменялся
  useEffect(() => {
    if (isVk) return
    const tgTheme = ((webApp as any)?.colorScheme || 'light') as 'light' | 'dark'
    setPlatformTheme(tgTheme)
  }, [(webApp as any)?.colorScheme])

  const effectiveTheme: 'light' | 'dark' = theme === 'platform' ? platformTheme : theme

  // Применяем тему при любом изменении
  useEffect(() => {
    applyTheme(effectiveTheme)
  }, [effectiveTheme])

  const setTheme = (newTheme: Theme) => {
    setStoredTheme(newTheme)
    setThemeState(newTheme)
  }

  return {
    theme,
    effectiveTheme,
    setTheme,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light',
    isPlatform: theme === 'platform',
    platformTheme,
  }
}
